import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_HOST } from '../utils/constants';
import { getUserCapabilities } from '../utils/userRoles';

const UserContext = createContext();

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem('app_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const storeAuth = (token, user) => {
  localStorage.setItem('app_token', token || '');
  localStorage.setItem('app_user', JSON.stringify(user || null));
  if (user?.name) {
    localStorage.setItem('app_user_id', user.name);
    localStorage.setItem('app_user_role', user.role || 'member');
  }
};

const clearAuth = () => {
  localStorage.removeItem('app_token');
  localStorage.removeItem('app_user');
  localStorage.removeItem('app_user_role');
};

async function parseApiResult(response) {
  const text = await response.text();
  let body;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(text || `HTTP ${response.status}`);
  }
  if (!response.ok || (body && typeof body.code === 'number' && body.code !== 200)) {
    const error = new Error(body?.message || `HTTP ${response.status}`);
    error.code = body?.code || response.status;
    error.data = body?.data;
    throw error;
  }
  return body?.data ?? body;
}

export const UserProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('app_token') || '');
  const [user, setUser] = useState(readStoredUser());
  const [initialized, setInitialized] = useState(false);

  const userId = user?.name || localStorage.getItem('app_user_id') || 'default_user';
  const role = user?.role || localStorage.getItem('app_user_role') || 'member';
  const { isAdmin, isOperations, isDirector, canAccessOperations } = getUserCapabilities({ name: userId, role });

  const applyLogin = useCallback((nextToken, nextUser) => {
    if (nextUser?.role === 'director') localStorage.setItem('home_mode', 'list');
    setToken(nextToken || '');
    setUser(nextUser || null);
    storeAuth(nextToken, nextUser);
  }, []);

  const refreshMe = useCallback(async () => {
    const currentToken = localStorage.getItem('app_token') || '';
    if (!currentToken) {
      setInitialized(true);
      return null;
    }
    try {
      const response = await fetch(`${API_HOST}/api/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      const me = await parseApiResult(response);
      setToken(currentToken);
      setUser(me);
      storeAuth(currentToken, me);
      return me;
    } catch (error) {
      if (error?.code === 401 || error?.code === 403) {
        clearAuth();
        setToken('');
        setUser(null);
      } else {
        setToken(currentToken);
        setUser(readStoredUser());
      }
      return null;
    } finally {
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const loginWithPassword = useCallback(async ({ username, password, firstSetup = false }) => {
    const response = await fetch(`${API_HOST}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, firstSetup }),
    });
    const data = await parseApiResult(response);
    applyLogin(data.token, data.user);
    return data;
  }, [applyLogin]);

  const login = useCallback((name, skipReload = false) => {
    // 兼容旧调用。新体系下只更新本地工作区名，不签发 token。
    const nextUser = { ...(user || {}), name, role };
    setUser(nextUser);
    localStorage.setItem('app_user_id', name);
    localStorage.setItem('app_user', JSON.stringify(nextUser));
    if (!skipReload) window.location.reload();
  }, [role, user]);

  const logout = useCallback(async () => {
    const currentToken = localStorage.getItem('app_token') || '';
    if (currentToken) {
      try {
        await fetch(`${API_HOST}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${currentToken}` },
        });
      } catch {
        // 本地退出优先。
      }
    }
    clearAuth();
    setToken('');
    setUser(null);
    window.location.href = '/';
  }, []);

  const value = useMemo(() => ({
    initialized,
    token,
    user,
    userId,
    role,
    isAdmin,
    isOperations,
    isDirector,
    canAccessOperations,
    login,
    loginWithPassword,
    logout,
    refreshMe,
  }), [initialized, token, user, userId, role, isAdmin, isOperations, isDirector, canAccessOperations, login, loginWithPassword, logout, refreshMe]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
